import { AppointmentBlockDto } from './transport-appointment.dto';
import { TransportStatus, LegPointType } from '@enums/transport.enum';
import { FuelType } from '@enums/vehicle.enum';
import { Helpers } from '@utils/helpers';

export class TransportDetailGetter {
  id: number = 0;
  transport_principal_type: 'NORMAL' | 'STAGE' = 'NORMAL';
  billing_account: {billing_account_id: number} | null = null;
  transport_status: TransportStatus = TransportStatus.PENDING;
  comment: string | null = null;
  reference_number: string | null = null;
  reservation_number: string | null = null;
  is_blocked: boolean = false;
  is_mission_blocked: boolean = false;
  invoiced: boolean = false;
  cancelled_comment: string | null = null;
  cancelled_reason: string | null = null;
  kilometers: string = '';
  price: string = '';
  manual_price: number = 0;
  has_manual_price: boolean = false;
  stage_discount: string = '0.00';
  duration: number = 0;
  created_date: string = '';
  modified_date: string = '';
  start_date: string | null = null;
  end_date: string | null = null;
  expected_start_date: string | null = null;
  expected_end_date: string | null = null;
  client: TransportClientGetter = new TransportClientGetter();
  admin: TransportAdminGetter | null = null;
  driver: TransportDriverGetter | null = null;
  vehicle: TransportVehicleGetter | null = null;
  tags: number[] = [];
  legs: TransportLegGetter[] = [];
  additional_services: AdditionalServiceSummaryGetter[] = [];
  origin_address: string = '';
  destination_address: string = '';
  has_appointment: boolean = false;
  appointment_status: string | null = null;
  appointment: AppointmentBlockDto | null = null;
  get appointment_management(): boolean { return this.has_appointment; }
  set appointment_management(val: boolean) { this.has_appointment = val; }
  group_number: string | null = null;
  countries: string[] = [];
  show_timeline: boolean = false;
  timeline: TransportEventGetter[] | null = null;
  change_requests: TransportChangeRequestGetter | null = null;
  best_budget: any | null = null;
  num_budgets: number = 0;
  related_transport_id: number | null = null;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportDetailGetter {
    return new TransportDetailGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      transport_principal_type: this.transport_principal_type,
      transport_status: this.transport_status,
      billing_account: this.billing_account,
      comment: this.comment,
      reference_number: this.reference_number,
      reservation_number: this.reservation_number,
      is_blocked: this.is_blocked,
      is_mission_blocked: this.is_mission_blocked,
      invoiced: this.invoiced,
      cancelled_comment: this.cancelled_comment,
      cancelled_reason: this.cancelled_reason,
      kilometers: this.kilometers,
      price: this.price,
      manual_price: this.manual_price,
      has_manual_price: this.has_manual_price,
      stage_discount: this.stage_discount,
      duration: this.duration,
      created_date: this.created_date,
      modified_date: this.modified_date,
      start_date: this.start_date,
      end_date: this.end_date,
      expected_start_date: this.expected_start_date,
      expected_end_date: this.expected_end_date,
      client: this.client.toJson(),
      admin: this.admin?.toJson() ?? null,
      driver: this.driver?.toJson() ?? null,
      vehicle: this.vehicle?.toJson() ?? null,
      tags: this.tags,
      legs: this.legs.map(leg => leg.toJson()),
      additional_services: this.additional_services.map(s => s.toJson()),
      origin_address: this.origin_address,
      destination_address: this.destination_address,
      has_appointment: this.has_appointment,
      appointment_status: this.appointment_status,
      appointment: this.appointment,
      appointment_management: this.has_appointment,
      group_number: this.group_number,
      countries: this.countries,
      show_timeline: this.show_timeline,
      timeline: this.timeline?.map(e => e.toJson()) ?? null,
      change_requests: this.change_requests?.toJson() ?? null,
      best_budget: this.best_budget,
      num_budgets: this.num_budgets,
      related_transport_id: this.related_transport_id
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.transport_principal_type = json.transport_principal_type ?? this.transport_principal_type;
    this.transport_status = json.transport_status ?? this.transport_status;
    this.billing_account = json.billing_account ?? this.billing_account;
    this.comment = json.comment ?? this.comment;
    this.reference_number = json.reference_number ?? this.reference_number;
    this.reservation_number = json.reservation_number ?? this.reservation_number;
    this.is_blocked = json.is_blocked ?? this.is_blocked;
    this.is_mission_blocked = json.is_mission_blocked ?? this.is_mission_blocked;
    this.invoiced = json.invoiced ?? this.invoiced;
    this.cancelled_comment = json.cancelled_comment ?? this.cancelled_comment;
    this.cancelled_reason = json.cancelled_reason ?? this.cancelled_reason;
    this.kilometers = json.kilometers ?? this.kilometers;
    this.price = json.price ?? this.price;
    this.manual_price = json.manual_price ?? this.manual_price;
    this.has_manual_price = json.has_manual_price ?? this.has_manual_price;
    this.stage_discount = json.stage_discount !== undefined ? String(json.stage_discount) : this.stage_discount;
    this.duration = json.duration ?? this.duration;
    this.created_date = json.created_date ?? this.created_date;
    this.modified_date = json.modified_date ?? this.modified_date;
    this.start_date = json.start_date ?? this.start_date;
    this.end_date = json.end_date ?? this.end_date;
    this.expected_start_date = json.expected_start_date ?? this.expected_start_date;
    this.expected_end_date = json.expected_end_date ?? this.expected_end_date;
    this.client = json.client ? new TransportClientGetter(json.client) : this.client;
    this.admin = json.admin ? new TransportAdminGetter(json.admin) : this.admin;
    this.driver = json.driver ? new TransportDriverGetter(json.driver) : this.driver;
    this.vehicle = json.vehicle ? new TransportVehicleGetter(json.vehicle) : this.vehicle;
    this.tags = json.tags ?? this.tags;
    this.legs = json.legs?.map((leg: any) => new TransportLegGetter(leg)) ?? this.legs;
    this.additional_services = json.additional_services?.map((s: any) => new AdditionalServiceSummaryGetter(s)) ?? this.additional_services;
    this.origin_address = json.origin_address ?? this.origin_address;
    this.destination_address = json.destination_address ?? this.destination_address;
    this.has_appointment = json.has_appointment ?? json.appointment_management ?? this.has_appointment;
    this.appointment_status = json.appointment_status ?? this.appointment_status;
    this.appointment = json.appointment ? AppointmentBlockDto.fromJson(json.appointment) : (json.appointment === null ? null : this.appointment);
    this.group_number = json.group_number ?? this.group_number;
    this.countries = json.countries ?? this.countries;
    this.show_timeline = json.show_timeline ?? this.show_timeline;
    this.timeline = json.timeline?.map((e: any) => new TransportEventGetter(e)) ?? this.timeline;
    this.change_requests = json.change_requests ? new TransportChangeRequestGetter(json.change_requests) : this.change_requests;
    this.best_budget = json.best_budget ?? this.best_budget;
    this.num_budgets = json.num_budgets ?? this.num_budgets;
    this.has_manual_price = Helpers.isNullOrUndefined(json.has_manual_price) ? this.has_manual_price : json.has_manual_price;
    this.manual_price = Helpers.isNullOrUndefined(json.manual_price) ? this.manual_price : json.manual_price;
    this.related_transport_id = json.related_transport_id ?? this.related_transport_id;
  }
}

export class TransportClientGetter {
  id: number = 0;
  name: string = '';
  logo: string | null = null;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportClientGetter {
    return new TransportClientGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      name: this.name,
      logo: this.logo
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.name = json.name ?? this.name;
    this.logo = json.logo ?? this.logo;
  }
}

export class TransportAdminGetter {
  id: number = 0;
  name: string = '';

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportAdminGetter {
    return new TransportAdminGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      name: this.name
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.name = json.name ?? this.name;
  }
}

export class TransportDriverGetter {
  id: number = 0;
  name: string = '';
  last_name: string = '';
  image: string | null = null;
  dni: string | null = null;
  phone: string | null = null;
  email: string | null = null;
  validated: boolean = false;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportDriverGetter {
    return new TransportDriverGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      name: this.name,
      last_name: this.last_name,
      image: this.image,
      dni: this.dni,
      phone: this.phone,
      email: this.email,
      validated: this.validated
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.name = json.name ?? this.name;
    this.last_name = json.last_name ?? this.last_name;
    this.image = json.image ?? this.image;
    this.dni = json.dni ?? this.dni;
    this.phone = json.phone ?? this.phone;
    this.email = json.email ?? this.email;
    this.validated = json.validated ?? this.validated;
  }
}

export class TransportVehicleGetter {
  id: number = 0;
  license_plate: string = '';
  brand: string = '';
  model: string = '';
  fuel_type: FuelType = FuelType.DIESEL;
  image: string | null = null;
  insurance_date_start: string | null = null;
  insurance_date_end: string | null = null;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportVehicleGetter {
    return new TransportVehicleGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      license_plate: this.license_plate,
      brand: this.brand,
      model: this.model,
      fuel_type: this.fuel_type,
      image: this.image,
      insurance_date_start: this.insurance_date_start,
      insurance_date_end: this.insurance_date_end
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.license_plate = json.license_plate ?? this.license_plate;
    this.brand = json.brand ?? this.brand;
    this.model = json.model ?? this.model;
    this.fuel_type = json.fuel_type ?? this.fuel_type;
    this.image = json.image ?? this.image;
    this.insurance_date_start = json.insurance_date_start ?? this.insurance_date_start;
    this.insurance_date_end = json.insurance_date_end ?? this.insurance_date_end;
  }
}

export class TransportAddressGetter {
  id: number = 0;
  street_address: string = '';
  postal_code: string = '';
  city: string = '';
  province: string = '';
  country: string = '';
  latitude: number = 0;
  longitude: number = 0;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportAddressGetter {
    return new TransportAddressGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      street_address: this.street_address,
      postal_code: this.postal_code,
      city: this.city,
      province: this.province,
      country: this.country,
      latitude: this.latitude,
      longitude: this.longitude
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.street_address = json.street_address ?? this.street_address;
    this.postal_code = json.postal_code ?? this.postal_code;
    this.city = json.city ?? this.city;
    this.province = json.province ?? this.province;
    this.country = json.country ?? this.country;
    this.latitude = json.latitude ?? this.latitude;
    this.longitude = json.longitude ?? this.longitude;
  }
}

export class TransportLegGetter {
  id: number = 0;
  order: number = 1;
  duration: number = 0;
  kilometers: number = 0;
  expected_departure_from: string = '';
  expected_departure_to: string = '';
  expected_arrival_from: string = '';
  expected_arrival_to: string = '';
  origin_address: TransportAddressGetter = new TransportAddressGetter();
  destination_address: TransportAddressGetter = new TransportAddressGetter();
  connection_type: LegPointType = LegPointType.STAGE;
  price: number = 0;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportLegGetter {
    return new TransportLegGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      order: this.order,
      duration: this.duration,
      kilometers: this.kilometers,
      expected_departure_from: this.expected_departure_from,
      expected_departure_to: this.expected_departure_to,
      expected_arrival_from: this.expected_arrival_from,
      expected_arrival_to: this.expected_arrival_to,
      origin_address: this.origin_address.toJson(),
      destination_address: this.destination_address.toJson(),
      connection_type: this.connection_type,
      price: this.price
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.order = json.order ?? this.order;
    this.duration = json.duration ?? this.duration;
    this.kilometers = json.kilometers ?? this.kilometers;
    this.expected_departure_from = json.expected_departure_from ?? this.expected_departure_from;
    this.expected_departure_to = json.expected_departure_to ?? this.expected_departure_to;
    this.expected_arrival_from = json.expected_arrival_from ?? this.expected_arrival_from;
    this.expected_arrival_to = json.expected_arrival_to ?? this.expected_arrival_to;
    this.origin_address = json.origin_address ? new TransportAddressGetter(json.origin_address) : this.origin_address;
    this.destination_address = json.destination_address ? new TransportAddressGetter(json.destination_address) : this.destination_address;
    this.connection_type = json.connection_type ?? this.connection_type;
    this.price = json.price ?? this.price;
  }
}

export class AdditionalServiceSummaryGetter {
  id: number = 0;
  name: string = '';
  description: string = '';
  client_price: number = 0;
  requires_image: boolean = false;
  requires_location: boolean = false;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): AdditionalServiceSummaryGetter {
    return new AdditionalServiceSummaryGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      client_price: this.client_price,
      requires_image: this.requires_image,
      requires_location: this.requires_location
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.name = json.name ?? this.name;
    this.description = json.description ?? this.description;
    this.client_price = json.client_price ?? this.client_price;
    this.requires_image = json.requires_image ?? this.requires_image;
    this.requires_location = json.requires_location ?? this.requires_location;
  }
}

export class TransportEventGetter {
  id: number = 0;
  title: string = '';
  description: string = '';
  type: string = '';
  timestamp: string = '';
  created_by_name: string = '';

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportEventGetter {
    return new TransportEventGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      type: this.type,
      timestamp: this.timestamp,
      created_by_name: this.created_by_name
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.title = json.title ?? this.title;
    this.description = json.description ?? this.description;
    this.type = json.type ?? this.type;
    this.timestamp = json.timestamp ?? this.timestamp;
    this.created_by_name = json.created_by_name ?? this.created_by_name;
  }
}

export class TransportChangeRequestGetter {
  id: number = 0;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' = 'PENDING';
  change_types_display: string[] = [];
  requested_at: string = '';
  distance: number = 0;

  constructor(data: any = {}) {
    this.copyFrom(data);
  }

  static fromJson(json: any): TransportChangeRequestGetter {
    return new TransportChangeRequestGetter(json);
  }

  toJson(): any {
    return {
      id: this.id,
      status: this.status,
      change_types_display: this.change_types_display,
      requested_at: this.requested_at,
      distance: this.distance
    };
  }

  copyFrom(json: any): void {
    if (!json) return;
    this.id = json.id ?? this.id;
    this.status = json.status ?? this.status;
    this.change_types_display = json.change_types_display ?? this.change_types_display;
    this.requested_at = json.requested_at ?? this.requested_at;
    this.distance = json.distance ?? this.distance;
  }
}


